/**
 * Snapping that explains itself.
 *
 * §6.3: guides appear with a label - *centre*, *aligned to logo*, *3 mm from
 * trim* - rather than a bare pink line. So a snap is not just a number here; it
 * carries the reason it happened, and the reason is what gets drawn.
 *
 * Everything in this file is in millimetres. The tolerance is the one exception
 * that has to know about zoom: a snap should feel like the same distance on
 * screen whether the customer is looking at the whole card or at one corner of
 * it, so it is defined in screen pixels and converted.
 */

import type { Layer, Page } from '../model/types';
import { flatten, foldsOf, guidesOf } from '../model/types';
import { formatLength, pxToMm, type Unit } from '../model/units';
import { useEditor } from '../state/store';

/** How close, in screen pixels, before a snap takes hold. */
const TOLERANCE_PX = 7;

export type Axis = 'x' | 'y';

export interface Guide {
  axis: Axis;
  /** Where the line sits, in mm. */
  at: number;
  /** What to say about it. */
  label: string;
  /** How far the line should run, in mm, so it reaches what it aligns to. */
  from: number;
  to: number;
}

interface Candidate {
  /** The coordinate being matched, in mm. */
  at: number;
  label: string;
  /** What the guide should span, so it visibly touches its reason. */
  from: number;
  to: number;
  /** Lower wins a tie. A fold beats page geometry, which beats another object. */
  rank: number;
}

export interface Snapped {
  x: number;
  y: number;
  guides: Guide[];
}

/** The three points on a box that can snap: its edges and its middle. */
function points(size: number): Array<{ offset: number; what: 'start' | 'middle' | 'end' }> {
  return [
    { offset: 0, what: 'start' },
    { offset: size / 2, what: 'middle' },
    { offset: size, what: 'end' },
  ];
}

/**
 * Where a layer should sit, given where the customer has dragged it.
 *
 * @param moving   The layer being dragged, at its proposed position.
 * @param page     The page it is on.
 * @param bleed    Document bleed, mm.
 * @param safe     Document safe margin, mm.
 * @param zoom     Current zoom, for the tolerance.
 * @param ignore   Layer ids that are moving too, so they do not snap to
 *                 themselves or to each other.
 */
export function snap(
  moving: { x: number; y: number; w: number; h: number },
  page: Page,
  safe: number,
  zoom: number,
  ignore: Set<string>
): Snapped {
  const tolerance = pxToMm(TOLERANCE_PX, zoom);

  const x = best('x', moving.x, moving.w, moving.h, moving.y, page, safe, ignore, tolerance);
  const y = best('y', moving.y, moving.h, moving.w, moving.x, page, safe, ignore, tolerance);

  return {
    x: x.value,
    y: y.value,
    guides: [...x.guides, ...y.guides],
  };
}

function best(
  axis: Axis,
  proposed: number,
  size: number,
  crossSize: number,
  crossStart: number,
  page: Page,
  safe: number,
  ignore: Set<string>,
  tolerance: number
): { value: number; guides: Guide[] } {
  const candidates = targets(axis, page, safe, ignore);

  let winner: { value: number; guide: Guide; distance: number; rank: number } | null = null;

  for (const point of points(size)) {
    const edge = proposed + point.offset;

    for (const candidate of candidates) {
      const distance = Math.abs(edge - candidate.at);

      if (distance > tolerance) continue;

      // Closest wins; on a tie, the lower rank - page geometry over an object,
      // because "centred on the card" is what a customer meant more often than
      // "level with that rectangle".
      if (winner && (winner.distance < distance || (winner.distance === distance && winner.rank <= candidate.rank))) {
        continue;
      }

      winner = {
        value: candidate.at - point.offset,
        rank: candidate.rank,
        distance,
        guide: {
          axis,
          at: candidate.at,
          label: label(candidate, point.what),
          // The guide runs from whatever it aligns to across to the layer
          // being moved, so the line visibly connects the two.
          from: Math.min(candidate.from, crossStart),
          to: Math.max(candidate.to, crossStart + crossSize),
        },
      };
    }
  }

  return winner ? { value: winner.value, guides: [winner.guide] } : { value: proposed, guides: [] };
}

/**
 * Where a guide being dragged off the ruler should land.
 *
 * The same targets an object snaps to, so a guide can be put exactly on the
 * trim, the centre, a fold or the safe line without aiming at a pixel.
 *
 * @param axis 'x' for a vertical guide, 'y' for a horizontal one.
 * @param at   Where the pointer is, in mm.
 */
export function snapGuide(
  axis: Axis,
  at: number,
  page: Page,
  safe: number,
  zoom: number
): { at: number; guides: Guide[] } {
  const tolerance = pxToMm(TOLERANCE_PX, zoom);
  const across = axis === 'x' ? page.size.h : page.size.w;

  let winner: { at: number; distance: number; rank: number; label: string } | null = null;

  for (const candidate of targets(axis, page, safe, new Set())) {
    // A guide does not snap to another guide: two lines a hair apart are
    // usually deliberate, and stacking them helps nobody.
    if (candidate.label === 'guide') continue;

    const distance = Math.abs(at - candidate.at);

    if (distance > tolerance) continue;

    if (winner && (winner.distance < distance || (winner.distance === distance && winner.rank <= candidate.rank))) continue;

    winner = { at: candidate.at, distance, rank: candidate.rank, label: candidate.label };
  }

  if (!winner) return { at, guides: [] };

  return { at: winner.at, guides: [{ axis, at: winner.at, label: winner.label, from: 0, to: across }] };
}

/** Everything on this axis worth snapping to. */
function targets(axis: Axis, page: Page, safe: number, ignore: Set<string>): Candidate[] {
  const size = axis === 'x' ? page.size.w : page.size.h;
  const across = axis === 'x' ? page.size.h : page.size.w;

  const out: Candidate[] = [
    { at: 0, label: 'trim', from: 0, to: across, rank: 0 },
    { at: size, label: 'trim', from: 0, to: across, rank: 0 },
    { at: size / 2, label: 'centre', from: 0, to: across, rank: 0 },
    { at: safe, label: `${formatLength(safe, unitNow())} from trim`, from: 0, to: across, rank: 1 },
    { at: size - safe, label: `${formatLength(safe, unitNow())} from trim`, from: 0, to: across, rank: 1 },
  ];

  /*
   * The folds, and the margin either side of each.
   *
   * A fold is worth snapping to twice over: things are lined up against a
   * crease all the time, and everything else must stay clear of it - so the
   * fold itself and the safe distance from it are both offered.
   *
   * The crease outranks the page's own geometry, which matters on a bi-fold
   * card: it folds down its middle, so the fold and the centre line are the
   * same coordinate, and of the two reasons "fold" is the one the customer is
   * working to - the centre of that card is a crease before it is a centre.
   */
  /*
   * The guides the customer dragged off the ruler.
   *
   * They outrank everything: someone who placed a line to work to meant it, so
   * it wins over the page's own centre and over a fold that happens to sit at
   * the same millimetre.
   */
  for (const at of guidesOf(page)[axis]) {
    out.push({ at, label: 'guide', from: 0, to: across, rank: -2 });
  }

  const folds = foldsNow();
  const creases = axis === 'x' ? folds.across : folds.down;

  for (const at of creases) {
    if (at <= 0 || at >= size) continue;

    out.push({ at, label: 'fold', from: 0, to: across, rank: -1 });

    if (safe > 0) {
      out.push(
        { at: at - safe, label: `${formatLength(safe, unitNow())} from the fold`, from: 0, to: across, rank: 1 },
        { at: at + safe, label: `${formatLength(safe, unitNow())} from the fold`, from: 0, to: across, rank: 1 }
      );
    }
  }

  for (const layer of flatten(page.layers)) {
    if (ignore.has(layer.id) || layer.hidden) continue;

    const start = axis === 'x' ? layer.x : layer.y;
    const extent = axis === 'x' ? layer.w : layer.h;
    const crossStart = axis === 'x' ? layer.y : layer.x;
    const crossExtent = axis === 'x' ? layer.h : layer.w;

    const named = layer.name || describe(layer);
    const from = crossStart;
    const to = crossStart + crossExtent;

    out.push(
      { at: start, label: `aligned to ${named}`, from, to, rank: 2 },
      { at: start + extent / 2, label: `centred on ${named}`, from, to, rank: 2 },
      { at: start + extent, label: `aligned to ${named}`, from, to, rank: 2 }
    );
  }

  return out;
}

/**
 * What the guide says.
 *
 * The page's own centre line gets the shorter phrasing, because "centre" on the
 * middle of the card means something a customer already understands, while
 * "centre" against another object needs to say which object.
 */
function label(candidate: Candidate, what: 'start' | 'middle' | 'end'): string {
  if (candidate.label === 'centre' && what === 'middle') return 'centre';
  if (candidate.label === 'centre') return 'card centre';

  return candidate.label;
}

function describe(layer: Layer): string {
  switch (layer.type) {
    case 'text':
      return 'the text';
    case 'image':
      return 'the picture';
    case 'shape':
      return 'the shape';
    case 'qr':
      return 'the QR code';
    default:
      return 'that object';
  }
}

/** The customer's unit, for the words on a guide. Read at snap time, not bound. */
function unitNow(): Unit {
  return useEditor.getState().unit;
}

/** Where the sheet folds. Read at snap time, like the unit above. */
function foldsNow(): { across: number[]; down: number[] } {
  const doc = useEditor.getState().doc;

  return doc ? foldsOf(doc, doc.pages[useEditor.getState().pageIndex]?.size ?? { w: 0, h: 0 }) : { across: [], down: [] };
}
